import {
    debounceTime, distinctUntilChanged, map, merge, MonoTypeOperatorFunction, OperatorFunction, skip, take, tap, SubscriptionLike,
    repeat,
    Observable,
    of,
    isObservable,
    from,
    throwError,
    mergeMap,
    toArray
} from "rxjs";
import { IAppService } from "../application/interfaces/app-service";
import { InvalidParametersException } from "../exceptions/invalid-parameters-exception";
import { ObjectUtil } from "@shopliftr/common-js/data";

/**
 * Operators and helper functions used to work with RXJS
 */
export class RxjsUtil {

    /**
     * Maximum number of page requests in flight at once when fetching the remaining pages of a paged search.
     */
    static readonly MAX_CONCURRENT_PAGES = 4;

    /**
     * Maximum number of pages (including the first) that a "get all" fan-out may request.
     * With a page size of 100 this allows up to 10,000 records.
     */
    static readonly MAX_PAGES = 100;

    /**
     * Fetches the pages that follow the first page of a paged search with bounded concurrency.
     *
     * At most {@link RxjsUtil.MAX_CONCURRENT_PAGES} requests are in flight at any time, and pages that
     * have not been sent yet are never sent if the subscriber unsubscribes. Pages are returned ordered by
     * their offset regardless of completion order. If the total requires more than {@link RxjsUtil.MAX_PAGES}
     * pages, no requests are issued and the observable errors with a descriptive error.
     *
     * @param total total number of records reported by the first page
     * @param pageSize size of each page (the first page has already been fetched)
     * @param fetchPage$ function that fetches the page starting at the given `from` offset
     * @param mapError optional function used to route the cap error through the caller's error handling
     * @returns observable of the remaining pages in offset order (empty when there are none)
     */
    static fetchRemainingPages$<R>(
        total: number,
        pageSize: number,
        fetchPage$: (from: number) => Observable<R>,
        mapError: (error: Error) => unknown = (error) => error
    ): Observable<Array<R>> {

        const pageCount = Math.ceil(total / pageSize);

        if (pageCount > RxjsUtil.MAX_PAGES) {
            return throwError(() => mapError(new Error(
                `Unable to retrieve all records: ${total} records require ${pageCount} pages, which exceeds the maximum of ${RxjsUtil.MAX_PAGES} pages`
            )));
        }

        const offsets = new Array<number>();
        for (let page = 1; page < pageCount; page++) {
            offsets.push(page * pageSize);
        }

        return from(offsets).pipe(
            mergeMap(
                (offset) => fetchPage$(offset).pipe(map((result) => ({
                    offset,
                    result
                }))),
                RxjsUtil.MAX_CONCURRENT_PAGES
            ),
            toArray(),
            map((pages) => pages
                .sort((a, b) => a.offset - b.offset)
                .map((page) => page.result))
        );
    }

    /**
     * Adding this operator to your pipe will activate the processing Application State when the observable is subscribed
     * and will deactivate when it completes or errors.
     *
     * Setting the processing app state will block user interaction with forms, buttons, etc while the operation is executing.
     *
     * example
     * ```
     * productService.search(filterOptions).pipe(RxjsUtils.appProcessingUntilDone(this.appState)).subscribe(...)
     * ```
     */
    static appProcessingUntilDone<T = unknown>(appState: IAppService): MonoTypeOperatorFunction<T> {

        if (!appState) {
            throw new InvalidParametersException("appState service is required");
        }
        return tap({
            subscribe: () => appState.processingStart(),
            finalize: () => appState.processingStop()
        });
    }

    /**
     * Will only emit an event if the object emitted has changed after deep inspection
     */
    static distinctUntilObjectChanged<T extends object>(): MonoTypeOperatorFunction<T> {

        return distinctUntilChanged<T>((prev, curr) => {
            return ObjectUtil.equals(prev, curr);
        });
    }

    /**
     * Will map the observable to emit void
     */
    static mapToVoid(): OperatorFunction<unknown, void> {

        return map((): void => undefined);
    }

    /**
     * Repeats the source observable each time it completes, until the provided
     * `Subscription` or `Subject` signals that it is finished.
     *
     * Typical use: keep a pipeline alive across an inner `takeUntil` completion
     * (e.g. a debounced blur validator that must restart after every option selection).
     *
     * ## Stop condition: BOTH `closed` and `isStopped` are checked
     *
     * The delay function treats the operator as "done" if EITHER of the following is true:
     *
     * - `closed === true`   — set by `Subscription.unsubscribe()` or `Subject.unsubscribe()`
     * - `isStopped === true` — set by `Subject.complete()` or `Subject.error()`
     *
     * Both checks are required because `Subscription` and `Subject` reach their terminal
     * state through different code paths:
     *
     * - `Subscription.unsubscribe()`   → flips `closed` to `true`
     * - `Subject.unsubscribe()`        → flips `closed` to `true`
     * - `Subject.complete()` / `error()` → flips `isStopped` to `true` but leaves `closed` as `false`
     *
     * ## Consequence of checking only `closed` (the previous bug)
     *
     * If a caller passes a `Subject` that is finalized via `.complete()` — a common
     * Angular `ngOnDestroy` pattern — and the delay function only checks `closed`, it
     * returns a non-empty notifier. That causes `repeat` to resubscribe to the now-stopped
     * `Subject`, which synchronously emits `complete` to each new subscriber. `repeat`
     * fires the delay again, which resubscribes again, producing an infinite synchronous
     * recursion that terminates with `RangeError: Maximum call stack size exceeded`.
     *
     * In practice this surfaced intermittently in the Jasmine `afterAll` hook when the
     * test bed tore down fixtures that still held active pipelines (notably
     * `GenericAutocompleteMatControlComponent`, which passes its own `Subject` as the guard).
     *
     * Checking `isStopped` in addition to `closed` makes the operator safe for both
     * `Subscription` and `Subject` guards.
     *
     * @param subscriptionLike subscription or subject whose lifetime bounds the repeat operation
     * @returns repeat operator that stops when the guard is unsubscribed or the subject completes
     */
    static repeatUntilClosed<T = unknown>(subscriptionLike: SubscriptionLike): MonoTypeOperatorFunction<T> {
        return repeat({
            delay: () => {
                // `isStopped` is specific to `Subject`; fall back to `false` for plain `Subscription`.
                const isStopped = (subscriptionLike as {isStopped?: boolean}).isStopped === true;
                if (subscriptionLike.closed || isStopped) {
                    return [];
                }
                return [true];
            }
        });
    }

    /**
     * Will take the value and return an observable
     *
     * The value can be a promise, observable or a value
     *
     * ### Finnish notation exception
     *
     * The `value` parameter is a polymorphic union (`T | Promise<T> | Observable<T>`).
     * A `$` suffix would be misleading for the non-Observable cases; `value` accurately
     * describes the universal input, so `@smarttools/rxjs/finnish` is disabled on the
     * signature line below.
     *
     * @param value
     * @returns observable
     */
    // eslint-disable-next-line @smarttools/rxjs/finnish
    static handleMaybeAsync$<T>(value: T | Promise<T> | Observable<T>): Observable<T> {
        if (value instanceof Promise) {
            return from(value);
        }
        else if (isObservable(value)) {
            return value;
        }
        else {
            return of(value);
        }
    }

    /**
     * Allows the first emission to pass through immediately, then debounces subsequent emissions
     *
     * @param debounceMs milliseconds to debounce after the first emission
     * @returns operator function
     */
    static debounceAfterInitial<T>(debounceMs: number): MonoTypeOperatorFunction<T> {
        return (source$: Observable<T>) => {
            return merge(
                source$.pipe(take(1)),
                source$.pipe(skip(1), debounceTime(debounceMs))
            );
        };
    }
}
